"use client";

import {
  getCurrency,
  getEntryFee,
  getPurse,
  getTournamentType,
  isPossibleToJoin,
} from "../../utils/tournaments";
import { useUI } from "../../context/UIContext";
import { utcTimeLocal } from "../../utils/time";
import Cta from "../Cta";
import dayjs from "dayjs";

interface OverviewProps {
  startDate: string;
  endDate: string;
  courseName: string;
  playersCount: number;
  tournamentName: string;
  tournamentSeason: number;
  tournamentId: string;
  configurationInfo: { displayFee: string; paymentUrl: string }[];
  signUpStartTime: string;
  signUpEndTime: string;
  isCTPRangeTournament?: boolean;
}

const Overview = ({
  endDate,
  startDate,
  courseName,
  playersCount,
  tournamentName,
  tournamentSeason,
  tournamentId,
  configurationInfo,
  signUpStartTime,
  signUpEndTime,
  isCTPRangeTournament,
}: OverviewProps) => {
  const { dispatch } = useUI();

  const signupUri =
    !!configurationInfo?.length && configurationInfo?.[0].paymentUrl;

  const type = getTournamentType(
    tournamentSeason,
    tournamentId,
    tournamentName
  );
  const currency = getCurrency(tournamentSeason, tournamentName);
  const purse = `${currency}${getPurse(
    tournamentSeason,
    tournamentId,
    tournamentName,
    playersCount
  ).toLocaleString("en-US")}`;

  const signupStart = dayjs(signUpStartTime);
  const signupEnd = dayjs(signUpEndTime);
  const currentDate = dayjs();

  return (
    <div className="flex flex-col gap-7.5 xl:ml-25">
      <div className="flex flex-col gap-2.5">
        <div className="text-[15px] tracking-[0.45px] leading-[1.6]">Date:</div>
        <div className="text-xl leading-normal tracking-[0.6px]">
          {utcTimeLocal(startDate, "MMM. D, H:mm z")} -{" "}
          {utcTimeLocal(endDate, "MMM. D, H:mm z, YYYY")}
        </div>
      </div>
      <div className="flex flex-col gap-2.5">
        <div className="text-[15px] tracking-[0.45px] leading-[1.6]">
          Entry fee:
        </div>
        <div className="text-xl leading-normal tracking-[0.6px]">
          {isCTPRangeTournament
            ? "-"
            : currency +
              getEntryFee(tournamentSeason, tournamentId, tournamentName)}
        </div>
      </div>
      <div className="flex flex-col gap-2.5">
        <div className="text-[15px] tracking-[0.45px] leading-[1.6]">
          Course:
        </div>
        <div className="text-xl leading-normal tracking-[0.6px]">
          {courseName}
        </div>
      </div>
      <div className="flex flex-col gap-2.5">
        <div className="text-[15px] tracking-[0.45px] leading-[1.6]">
          Purse:
        </div>
        <div className="text-xl leading-normal tracking-[0.6px]">
          {isCTPRangeTournament ? "-" : purse}
        </div>
      </div>
      <div className="flex flex-col gap-2.5">
        <div className="text-[15px] tracking-[0.45px] leading-[1.6]">
          Players:
        </div>
        <div className="text-xl leading-normal tracking-[0.6px]">
          {tournamentSeason === 1
            ? playersCount
            : type.isFinalSeason2
            ? 100
            : `${playersCount}${isCTPRangeTournament ? "" : " / 1000"}`}
        </div>
      </div>
      {signUpStartTime &&
        signUpEndTime &&
        currentDate.isAfter(signupStart) &&
        currentDate.isBefore(signupEnd) &&
        isPossibleToJoin(playersCount) &&
        signupUri && (
          <Cta
            name="Join tournament"
            className="mt-5 mx-auto mb-0 lg:mt-20 lg:mx-0"
            onClick={() => {
              window.localStorage.setItem("next_signup_uri", signupUri);
              dispatch({
                type: "toggleJoinFlowInfoModal",
                payload: {
                  isOpen: true,
                  title: tournamentName,
                  modalType: "signup",
                },
              });
            }}
          />
        )}
    </div>
  );
};

export default Overview;
